import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { LIVE_POLL_COPY } from '@components/livePolls/livePollCopy';
import { POLL_COPY } from '@components/polls/pollCopy';
import { toast } from '@hooks/use-toast';
import { LIVE_ERROR_CODES, liveStateFromRefusal, pollErrorCode } from '@lib/polls/pollErrors';
import type { LiveMe, LiveQuestion, LiveState } from '@models/api';

/** E2: the answer that did not reach the server, kept ticked until the tap that sends it again. */
export interface LiveAnswerFailure {
  position: number;
  optionIds: string[];
}

/** What a live card needs to answer: a tap on an option, E2's failed answer and its retry. */
export interface LiveAnswering {
  toggle: (optionId: string) => void;
  failed: LiveAnswerFailure | null;
  retry: () => void;
  /** The question whose answer is on its way (its tick and bars already show it: `ownChange`). */
  sendingPosition: number | null;
  /** What this viewer's own taps do to the figures until the server's carry them (`withOwnChange`). */
  ownChange?: LiveOwnChange | null;
  /** 403 `LIVE_CANNOT_ANSWER` came back: the room shows E6 ("You can't answer this poll"). */
  cannotAnswer: boolean;
}

/** One figure held where the viewer's tap put it: a raised one no lower, a dropped one no higher. */
export interface LiveFigureHold {
  atLeast?: number;
  atMost?: number;
}

/**
 * The viewer's own change, drawn before the server's figures carry it. Each figure the tap moved is HELD rather than
 * offset: a raised bar reads at least what the tap made it, a dropped one at most. A `/state` from before the change
 * then changes nothing (no flicker back) and one that already has it passes through (no double count), so the phone
 * never has to know which of the two it is holding. The server's figures rule again once they have moved on.
 */
export interface LiveOwnChange {
  position: number;
  /** The picks the drawn figures stand for. */
  picks: string[];
  /** By option id. */
  options: Record<string, LiveFigureHold>;
  voters: LiveFigureHold | null;
  /** When the server took the newest tap, and the state's `version` then. */
  confirmedAt: number | null;
  versionAtConfirm: number | null;
}

const held = (value: number, hold: LiveFigureHold | null | undefined): number => {
  if (hold?.atLeast !== undefined) return Math.max(value, hold.atLeast);
  if (hold?.atMost !== undefined) return Math.min(value, hold.atMost);
  return value;
};

/** A question's figures as this viewer should see them. */
export const withOwnChange = (
  question: LiveQuestion,
  change: LiveOwnChange | null | undefined,
): LiveQuestion =>
  !change || change.position !== question.position
    ? question
    : {
        ...question,
        voters_count: held(question.voters_count, change.voters),
        options: question.options.map(option => ({
          ...option,
          votes_count: held(option.votes_count, change.options[option.id]),
        })),
      };

/** The change after a tap from `before` to `after`, built on the figures on screen. */
const nextOwnChange = (
  question: LiveQuestion,
  current: LiveOwnChange | null,
  before: string[],
  after: string[],
): LiveOwnChange => {
  const carried = current?.position === question.position ? current : null;
  const shown = withOwnChange(question, carried);
  const options = { ...carried?.options };
  for (const { id, votes_count: votes } of shown.options) {
    const was = before.includes(id);
    const is = after.includes(id);
    if (was && !is) options[id] = { atMost: Math.max(0, votes - 1) };
    else if (!was && is) options[id] = { atLeast: votes + 1 };
  }
  let voters = carried?.voters ?? null;
  // One-answer or several: the person counts once, from their first pick to their last.
  if (before.length === 0 && after.length > 0) voters = { atLeast: shown.voters_count + 1 };
  else if (before.length > 0 && after.length === 0)
    voters = { atMost: Math.max(0, shown.voters_count - 1) };
  return {
    position: question.position,
    picks: after,
    options,
    voters,
    confirmedAt: null,
    versionAtConfirm: null,
  };
};

/**
 * `/state` is cached for 1 s at the edge (CONTRACT, cache headers), so a read this long after the server took the tap carries it;
 * from then on a newer `version` means the room's own figures can be trusted over the holds.
 */
const STATE_CACHE_MARGIN_MS = 1_500;

export interface LiveAnswerSent {
  /** The join Space a signed-in non-member's first answer joined (D3b), else null. */
  joinedSpaceId: string | null;
  /** The viewer's `LiveMe` when the route answers it (`/answer` does; the card's vote route does not). */
  me?: LiveMe | null;
}

export interface UseLiveAnswerOptions {
  /** One answer in flight at a time per poll, in order (the mutation scope). */
  scopeId: string;
  state: LiveState | null;
  me: LiveMe | null;
  send: (position: number, optionIds: string[]) => Promise<LiveAnswerSent>;
  /** Writes the viewer's picks for one question into the cache the card draws from, at once. */
  writePicks: (position: number, optionIds: string[]) => void;
  writeMe: (me: LiveMe) => void;
  /** A refusal's `details.state` (E1): the card jumps to the question open now. */
  writeState: (state: LiveState) => void;
  /** Reads the viewer's `LiveMe` again (after a refusal, so a pick that never landed goes). */
  rereadMe: () => void;
}

interface AnswerVariables {
  position: number;
  optionIds: string[];
  ticket: number;
  /** The one send made by itself after a 429's Retry-After (or a send that waited one out): no second. */
  resend: boolean;
}

/** A 429's wait is capped here (CONTRACT 10:20, 11:25 notes; the coordinator's FE-L3a note: ≤ 5 s). */
export const LIVE_ANSWER_MAX_WAIT_MS = 5_000;

/** The wait a 429 asks for (`Retry-After`, seconds), capped; 1 s when it names none. */
const busyWaitMs = (error: unknown): number => {
  const headers = (error as { response?: { headers?: Record<string, unknown> } } | null)?.response
    ?.headers;
  const raw = headers?.['retry-after'] ?? headers?.['Retry-After'];
  const seconds = typeof raw === 'string' || typeof raw === 'number' ? Number(raw) : NaN;
  const ms = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 1_000;
  return Math.min(ms, LIVE_ANSWER_MAX_WAIT_MS);
};

/** The time before which nothing is sent after a 429 (now + its capped wait). */
const busyUntil = (error: unknown): number => Date.now() + busyWaitMs(error);

/** The viewer's picks on one question, from `LiveMe`. */
export const livePicksAt = (me: LiveMe | null, position: number): string[] =>
  me?.my_answers.find(answer => answer.position === position)?.option_ids ?? [];

/** `me` with the picks for one question replaced (none: that question's answer taken back). */
export const withLivePicks = (me: LiveMe, position: number, optionIds: string[]): LiveMe => {
  const others = me.my_answers.filter(answer => answer.position !== position);
  const my_answers =
    optionIds.length > 0 ? [...others, { position, option_ids: optionIds }] : others;
  return { ...me, my_answers, answered_any: me.answered_any || optionIds.length > 0 };
};

/** 429 `LIVE_RATE_LIMITED`: the answer queue was busy, or the per-person / per-room limit (CONTRACT § 4.11). */
const isBusy = (error: unknown): boolean => pollErrorCode(error) === LIVE_ERROR_CODES.rateLimited;

/** A failure E2 covers: no answer at all, a 5xx, or the live rate limit (once its one resend failed too). */
const isSendFailure = (error: unknown): boolean => {
  if (isBusy(error)) return true;
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === undefined || status >= 500;
};

/**
 * Answering a live poll (TECH-867 § Answering, § Errors E1/E2; D3b), shared by the Post sheet (`/answer`, a
 * guest token) and a member's feed card (the vote route with `position`). The pick shows at once in the
 * cache the card draws from, and its effect on the figures in `ownChange`, until the 2 s read carries it.
 *
 * - Taps go out one at a time, in order; only the newest tap's outcome is shown.
 * - 409 `LIVE_QUESTION_CLOSED` (E1): info toast "That question closed. This is the one open now." and the card
 *   jumps to `details.state`; `/me` is read again.
 * - 429 `LIVE_RATE_LIMITED` (busy or the limit): sent again by itself ONCE after Retry-After (capped at 5 s), the
 *   pick shown as sending meanwhile; nothing goes before the wait is over (a tap's newest picks wait for it).
 * - No answer, a 5xx, a 429 after its resend (E2): the tick stays with "Didn't send. Tap to try again" inline
 *   under it, no toast; that tap sends the same picks again (`retry`), after any Retry-After still running.
 * - `joined_space_id` (D3b, L31): info toast "Your answer is in. You've joined {Space}."
 * - 403 `LIVE_CANNOT_ANSWER`: `cannotAnswer` (the room shows E6) and `/me` again (`can_answer` false).
 */
export function useLiveAnswer({
  scopeId,
  state,
  me,
  send,
  writePicks,
  writeMe,
  writeState,
  rereadMe,
}: UseLiveAnswerOptions): LiveAnswering {
  const [failed, setFailed] = useState<LiveAnswerFailure | null>(null);
  const [sendingPosition, setSendingPosition] = useState<number | null>(null);
  const [cannotAnswer, setCannotAnswer] = useState(false);
  const ticketRef = useRef(0);
  // A 429's Retry-After: nothing is sent before this time; the picks to send then, and the timer that sends them.
  const notBeforeRef = useRef(0);
  const waitingRef = useRef<{ position: number; optionIds: string[] } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The newest tap's picks: two taps before the cache re-renders still build on each other.
  const lastTapRef = useRef<{ position: number; optionIds: string[]; seen: LiveMe | null } | null>(
    null,
  );
  const joinSpaceName = state?.join_space?.name ?? null;
  // The ref is for two taps before a re-render, as `lastTapRef`.
  const [ownChange, setOwnChange] = useState<LiveOwnChange | null>(null);
  const ownChangeRef = useRef<LiveOwnChange | null>(null);
  // What the server last held for the question being answered: where a retry's change starts from after a failure.
  const serverPicksRef = useRef<{ position: number; optionIds: string[] } | null>(null);
  const showOwnChange = (next: LiveOwnChange | null) => {
    ownChangeRef.current = next;
    setOwnChange(next);
  };
  const stateVersion = state?.version;
  const trustTheRoom = useEffectEvent(() => {
    const change = ownChangeRef.current;
    if (
      change?.confirmedAt != null &&
      change.versionAtConfirm != null &&
      stateVersion !== undefined &&
      stateVersion > change.versionAtConfirm &&
      Date.now() >= change.confirmedAt + STATE_CACHE_MARGIN_MS
    ) {
      showOwnChange(null);
    }
  });
  // The room's read is the external system: its newer figures take over from the holds once they carry the tap.
  useEffect(() => {
    trustTheRoom();
  }, [stateVersion]);

  const { mutate } = useMutation<LiveAnswerSent, unknown, AnswerVariables>({
    scope: { id: `live-answer:${scopeId}` },
    // E2 is the retry: the person taps it.
    retry: false,
    // Offline the answer fails at once (E2), never paused until the network returns with no word.
    networkMode: 'always',
    mutationFn: ({ position, optionIds }) => send(position, optionIds),
    onSuccess: (sent, { position, optionIds, ticket }) => {
      serverPicksRef.current = { position, optionIds };
      if (ticket !== ticketRef.current) return;
      setFailed(null);
      if (ownChangeRef.current) {
        showOwnChange({
          ...ownChangeRef.current,
          confirmedAt: Date.now(),
          versionAtConfirm: stateVersion ?? 0,
        });
      }
      if (sent.me) writeMe(sent.me);
      if (sent.joinedSpaceId) {
        toast({
          title: joinSpaceName ? LIVE_POLL_COPY.answerInJoined(joinSpaceName) : POLL_COPY.answerIn,
          variant: 'info',
          id: `live-joined:${scopeId}`,
        });
      }
    },
    onSettled: (_data, _error, { ticket }) => {
      // Still sending while a resend waits out a Retry-After.
      if (ticket === ticketRef.current && waitingRef.current === null) setSendingPosition(null);
    },
    onError: (error, { position, optionIds, ticket, resend }) => {
      if (isBusy(error)) notBeforeRef.current = busyUntil(error);
      if (ticket !== ticketRef.current) return;
      if (isBusy(error) && !resend) {
        // Once, by itself, after the wait; the pick stays shown as sending.
        waitAndSend({ position, optionIds });
        return;
      }
      lastTapRef.current = null;
      // Nothing changed on the server: the figures go back to what it says.
      showOwnChange(null);
      const code = pollErrorCode(error);
      if (code === LIVE_ERROR_CODES.questionClosed) {
        setFailed(null);
        toast({
          title: LIVE_POLL_COPY.questionClosed,
          variant: 'info',
          id: `live-question-closed:${scopeId}`,
        });
        const fresh = liveStateFromRefusal(error);
        if (fresh) writeState(fresh);
        rereadMe();
        return;
      }
      if (isSendFailure(error)) {
        setFailed({ position, optionIds });
        return;
      }
      setFailed(null);
      if (code === LIVE_ERROR_CODES.cannotAnswer) setCannotAnswer(true);
      else
        toast({
          title: POLL_COPY.answerFailed,
          variant: 'error',
          id: `live-answer-failed:${scopeId}`,
        });
      rereadMe();
    },
  });

  // The timer is the external system; it never outlives the card.
  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  /** Sends `picks` (the newest by then) when the Retry-After is over, as the one resend. */
  function waitAndSend(picks: { position: number; optionIds: string[] }) {
    waitingRef.current = picks;
    if (timerRef.current !== null) return;
    timerRef.current = setTimeout(
      () => {
        timerRef.current = null;
        const waiting = waitingRef.current;
        waitingRef.current = null;
        if (!waiting) return;
        ticketRef.current += 1;
        mutate({ ...waiting, ticket: ticketRef.current, resend: true });
      },
      Math.max(0, notBeforeRef.current - Date.now()),
    );
  }

  /** What the figures on screen stand for, before this tap. */
  const picksShownAt = (position: number): string[] => {
    const change = ownChangeRef.current;
    if (change?.position === position) return change.picks;
    if (failed?.position === position) {
      const server = serverPicksRef.current;
      return server?.position === position ? server.optionIds : [];
    }
    return livePicksAt(me, position);
  };

  const sendPicks = (position: number, optionIds: string[]) => {
    ticketRef.current += 1;
    const before = picksShownAt(position);
    if (!ownChangeRef.current && failed?.position !== position)
      serverPicksRef.current = { position, optionIds: before };
    if (state?.question?.position === position) {
      showOwnChange(nextOwnChange(state.question, ownChangeRef.current, before, optionIds));
    }
    writePicks(position, optionIds);
    setFailed(null);
    setSendingPosition(position);
    if (Date.now() < notBeforeRef.current) {
      // A Retry-After is running: the tap's picks go when it is over, never early.
      waitAndSend({ position, optionIds });
      return;
    }
    mutate({ position, optionIds, ticket: ticketRef.current, resend: false });
  };

  const toggle = (optionId: string) => {
    const question = state?.question;
    const position = state?.current_position;
    if (state?.live_state !== 'live' || !question || position === null || position === undefined)
      return;
    const lastTap = lastTapRef.current;
    let before: string[];
    if (failed?.position === position) before = failed.optionIds;
    else if (lastTap && lastTap.position === position && lastTap.seen === me)
      before = lastTap.optionIds;
    else before = livePicksAt(me, position);
    const mine = new Set(before);
    const picked = mine.has(optionId);
    if (question.allows_multiple) {
      if (picked) mine.delete(optionId);
      else mine.add(optionId);
    } else {
      mine.clear();
      if (!picked) mine.add(optionId);
    }
    const optionIds = question.options
      .filter(option => mine.has(option.id))
      .map(option => option.id);
    lastTapRef.current = { position, optionIds, seen: me };
    sendPicks(position, optionIds);
  };

  const retry = () => {
    if (failed) sendPicks(failed.position, failed.optionIds);
  };

  return { toggle, failed, retry, sendingPosition, cannotAnswer, ownChange };
}
